import React, { useEffect, useState } from "react";
import axios from "axios";
import { getToken, clearSession } from "@/lib/sessionStore";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DemoBanner — Faixa fixa no topo, exibida SOMENTE quando o tenant
 * logado é demo. Design alinhado ao padrão premium AgentUS (sem
 * gradiente violeta / emojis playful). Foco:
 *  - Indicador discreto "AMBIENTE DE DEMONSTRAÇÃO" com dot verde
 *  - Nome da persona (Oficina / Concessionária)
 *  - Botão "Resetar dados" (rate-limited pelo backend em 5min)
 *  - Botão "Sair da demo" — limpa sessão e volta ao portal do rep
 */
export default function DemoBanner() {
  const [info, setInfo] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    axios
      .get(`${API}/demo/info`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setInfo(r.data))
      .catch(() => setInfo({ is_demo: false }));
  }, []);

  if (!info || !info.is_demo) return null;

  const personaLabel =
    info.demo_persona === "dealer" ? "Concessionária" : "Oficina";

  const doReset = async () => {
    if (!window.confirm(
      "Resetar todos os dados fictícios da demo?\n" +
      "Vai recriar clientes, conversas, agendamentos etc. do zero."
    )) return;
    setResetting(true);
    setMsg("");
    try {
      const token = getToken();
      const r = await axios.post(
        `${API}/demo/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const s = r.data?.seeded || {};
      setMsg(`Reset OK — ${s.customers || 0} clientes, ${s.conversations || 0} conversas.`);
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.reason === "cooldown") {
        setMsg(`Aguarde ${detail.wait_s}s para resetar novamente.`);
      } else {
        setMsg("Erro ao resetar. Tente novamente.");
      }
    } finally {
      setResetting(false);
    }
  };

  const exitDemo = () => {
    if (!window.confirm("Sair do modo demonstração e voltar ao portal?")) return;
    clearSession();
    localStorage.removeItem("demo_session_id");
    localStorage.removeItem("is_demo_mode");
    // ⚠️ A rota do rep é `/representante` (index), NÃO `/representante/dashboard`.
    // Se apontarmos pra rota inexistente, cai no catch-all Navigate→/app
    // e como o AgentUS acabou de ser deslogado, vira /login. Não é isso
    // que queremos — queremos o rep voltar pro PORTAL dele (autenticado
    // via `agentus_rep_token`, que segue intacto após clearSession).
    window.location.href = "/representante";
  };

  return (
    <div
      data-testid="demo-banner"
      className="sticky top-0 z-[9999] border-b border-white/10 bg-black/85 backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-6 py-2">
        <div className="flex items-center gap-3">
          <span className="relative inline-flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
          </span>
          <span className="text-[10px] uppercase tracking-[0.22em] font-medium text-slate-400">
            Ambiente de demonstração
          </span>
          <span className="hidden md:inline text-[11px] text-slate-500">·</span>
          <span className="hidden md:inline text-[12px] text-white font-medium tracking-tight">
            {personaLabel}
          </span>
          <span className="hidden lg:inline text-[11px] text-slate-500">
            · dados fictícios, nenhuma mensagem chega ao WhatsApp real
          </span>
        </div>

        <div className="flex items-center gap-2">
          {msg && (
            <span
              data-testid="demo-reset-msg"
              className="text-[11px] text-slate-400 hidden sm:inline"
            >
              {msg}
            </span>
          )}
          <button
            data-testid="demo-reset-btn"
            onClick={doReset}
            disabled={resetting}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-white/[0.14] bg-white/[0.03] hover:bg-white/[0.08] hover:border-white/25 text-white text-[11px] font-medium tracking-tight transition-all disabled:opacity-40 disabled:pointer-events-none"
          >
            {resetting ? "Resetando…" : "Resetar dados"}
          </button>
          <button
            data-testid="demo-exit-btn"
            onClick={exitDemo}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white text-black text-[11px] font-medium tracking-tight transition-all hover:bg-slate-100"
          >
            Sair da demo
          </button>
        </div>
      </div>
    </div>
  );
}
